// --- CONSTANTS from original component ---
const CLASS_MEDIA_OVERLAY = "cookies-info-overlay";
const IGNORED_IFRAMES_SELECTOR = ':not(.iframe-no-cookies):not([src*="awareness.cloud.apg.at"]):not([src*="app.23degrees.eu"]):not([data-23src*="app.23degrees.eu"]):not([src*="transparency.apg.at"])';
const IFRAMES_SELECTOR = `iframe${IGNORED_IFRAMES_SELECTOR}`;
const IFRAMES_SELECTOR_NO_COOKIES = `iframe:not(${IFRAMES_SELECTOR})`;

// Default message to display when content is blocked
const DEFAULT_ENABLE_MESSAGE = document.getElementById('page')?.dataset.cookieOverlayMessage ?? "Please enable cookies to view this content";

/**
 * Executes a function on a list of iframes and performs lazy-load update.
 * @param scopeElem The parent element to search within (defaults to document).
 * @param iframesSelector The CSS selector for the iframes (defaults to IFRAMES_SELECTOR).
 * @param handler A function to execute on each found iframe.
 * @param lazyLoadInstance A function to call to update the lazy-load library.
 */
function processIframes(scopeElem: ParentNode = document, iframesSelector: string = IFRAMES_SELECTOR, handler: (iframe: HTMLIFrameElement, messageEl: Element | null) => void, lazyLoadUpdate?: () => void): void {
    let iframesInScope: HTMLIFrameElement[] = [];

    if (scopeElem instanceof HTMLIFrameElement) {
        iframesInScope = [scopeElem];
    } else {
        // Cast to NodeListOf<HTMLIFrameElement> for correct typing
        const nodeList = scopeElem.querySelectorAll(iframesSelector) as NodeListOf<HTMLIFrameElement>;
        iframesInScope = Array.from(nodeList);
    }

    if (iframesInScope.length === 0) {
        return;
    }

    iframesInScope.forEach((iframe: HTMLIFrameElement) => {
        // Find any existing overlay message on the parent
        const message = iframe.parentNode?.querySelector(`.${CLASS_MEDIA_OVERLAY}`) ?? null;
        handler(iframe, message);
    });

    if (lazyLoadUpdate) {
        lazyLoadUpdate();
    }
}

/**
 * Removes the overlay and allows the iframe to load.
 * @param iframe The iframe element.
 * @param message The existing overlay element, if any.
 */
const allowIframeHandler = (iframe: HTMLIFrameElement, message: Element | null): void => {
    if (message) {
        message.parentNode?.removeChild(message);
    }
    iframe.setAttribute("lazy", "true"); // Restore 'lazy' attribute
    iframe.style.display = "block"; // Ensure visibility
};

/**
 * Adds an overlay to block the iframe and hides it.
 * @param iframe The iframe element.
 * @param message The existing overlay element, if any.
 * @param enableMessage The message to show in the overlay.
 * @param onOverlayClick The function to call when the overlay is clicked (to re-open banner).
 */
const disableIframeHandler = (iframe: HTMLIFrameElement, message: Element | null, enableMessage: string, onOverlayClick: () => void): void => {
    // If an overlay already exists, just return (avoid creating duplicates)
    if (message) return;

    const pTag = document.createElement("p");
    pTag.innerHTML = enableMessage;
    pTag.classList.add(`${CLASS_MEDIA_OVERLAY}-text`);
    const divTag = document.createElement("div");
    divTag.classList.add(CLASS_MEDIA_OVERLAY);
    divTag.appendChild(pTag);

    // Insert overlay before the iframe
    iframe.parentNode?.insertBefore(divTag.cloneNode(true), iframe);

    // Add click listener to the new overlay's text element
    iframe.parentNode?.querySelector(`.${CLASS_MEDIA_OVERLAY}-text`)?.addEventListener("click", onOverlayClick);

    // Hide the iframe itself
    iframe.style.display = "none";
};

/**
 * Allows (unblocks) scripts by changing their type and executing them.
 */
function allowScripts(): void {
    for (const script of Array.from(document.scripts)) {
        if (script.type.toLowerCase() === "text/plain") {
            const clonedScript = script.cloneNode(true) as HTMLScriptElement;
            const oldType = clonedScript.getAttribute("data-type");

            if (oldType) {
                clonedScript.setAttribute("type", oldType);
            } else {
                clonedScript.removeAttribute("type");
            }
            clonedScript.removeAttribute("data-type");

            // Replace the blocked script with the runnable script
            script.parentNode?.replaceChild(clonedScript, script);
        }
    }
}

/**
 * Allows iframes that require explicit consent (e.g., marketing iframes).
 */
function allowIFrames(scopeElem?: ParentNode, lazyLoadUpdate?: () => void): void {
    processIframes(scopeElem, IFRAMES_SELECTOR, allowIframeHandler, lazyLoadUpdate);
}

/**
 * Blocks iframes that require explicit consent.
 * @param enableMessage The message to show in the overlay.
 * @param onOverlayClick The function to call when the overlay is clicked (to re-open banner).
 */
function disableIFrames(enableMessage: string, onOverlayClick: () => void, scopeElem?: ParentNode, lazyLoadUpdate?: () => void): void {
    processIframes(scopeElem, IFRAMES_SELECTOR, (iframe, message) => disableIframeHandler(iframe, message, enableMessage, onOverlayClick), lazyLoadUpdate);
}

/**
 * Allows iframes that DO NOT require explicit consent.
 */
function allowNoConsentIFrames(scopeElem?: ParentNode, lazyLoadUpdate?: () => void): void {
    processIframes(scopeElem, IFRAMES_SELECTOR_NO_COOKIES, allowIframeHandler, lazyLoadUpdate);
}

/**
 * Blocks iframes that DO NOT require explicit consent.
 * @param enableMessage The message to show in the overlay.
 * @param onOverlayClick The function to call when the overlay is clicked (to re-open banner).
 */
function disableNoConsentIFrames(enableMessage: string, onOverlayClick: () => void, scopeElem?: ParentNode, lazyLoadUpdate?: () => void): void {
    processIframes(scopeElem, IFRAMES_SELECTOR_NO_COOKIES, (iframe, message) => disableIframeHandler(iframe, message, enableMessage, onOverlayClick), lazyLoadUpdate);
}

// --- NEW FUNCTION TO HANDLE BOTH ENABLE/DISABLE STATES ---

interface ConsentHandlerOptions {
    enableMessage?: string;
    onOverlayClick: () => void;
    lazyLoadUpdate?: () => void;
    scopeElem?: ParentNode;
}

/**
 * Central function to manage scripts and iframes based on a general consent state.
 * @param accepted True if general consent is granted; false otherwise.
 * @param options Configuration for messages and callbacks.
 */
function handleConsentState(accepted: boolean, options: ConsentHandlerOptions): void {
    const { enableMessage = DEFAULT_ENABLE_MESSAGE, onOverlayClick, lazyLoadUpdate, scopeElem = document } = options;

    if (accepted) {
        // 1. Allow scripts
        allowScripts();

        // 2. Allow iframes that require consent
        allowIFrames(scopeElem, lazyLoadUpdate);

        // 3. Allow no-consent iframes (Replicating the old component's 'cookiesClosed' state)
        // not applied since these use only technical cookies
        // allowNoConsentIFrames(scopeElem, lazyLoadUpdate);
    } else {
        // 1. Scripts are naturally blocked if they stay as type='text/plain'

        // 2. Disable iframes that require consent
        disableIFrames(enableMessage, onOverlayClick, scopeElem, lazyLoadUpdate);

        // 3. Disable no-consent iframes (Replicating the old component's 'cookiesClosed = false' state)
        // disableNoConsentIFrames(enableMessage, onOverlayClick, scopeElem, lazyLoadUpdate);
    }
}

declare global {
    interface Window {
        dr_cookiebanner_options?: {
            dr_onEnableCookies?: (enable: boolean) => void;
            [key: string]: unknown;
        };
        dr_revokeChoice?: () => void;
    }
}

export function initializeDatareporterConsentManager(lazyLoadUpdate?: () => void) {
    const reopenBanner = () => {
        window.dr_revokeChoice?.();
    };
    const handlerOptions = {
        enableMessage: DEFAULT_ENABLE_MESSAGE,
        onOverlayClick: reopenBanner,
        lazyLoadUpdate: lazyLoadUpdate,
    };

    let consentAccepted = false;

    const processAddedNode = (node: HTMLElement): void => {
        const hasIframes = node.matches(IFRAMES_SELECTOR) || !!node.querySelector(IFRAMES_SELECTOR);
        if (!hasIframes) {
            return;
        }
        if (consentAccepted) {
            allowIFrames(node, lazyLoadUpdate);
        } else {
            disableIFrames(DEFAULT_ENABLE_MESSAGE, reopenBanner, node, lazyLoadUpdate);
        }
    };

    // Initial check on page load
    if ((window as any).cookieconsent && typeof (window as any).cookieconsent.currentConsentStatus === "function") {
        const status = (window as any).cookieconsent.currentConsentStatus();
        // datareporter's currentConsentStatus() returns booleans (statistic/marketing/drAllow)
        consentAccepted = !!status.drAllow || !!status.marketing || !!status.statistic;
        handleConsentState(consentAccepted, handlerOptions);
    }

    if (typeof window.dr_cookiebanner_options === "undefined") {
        window.dr_cookiebanner_options = {};
    }

    window.dr_cookiebanner_options.dr_onEnableCookies = function (enable) {
        // The 'enable' parameter from dr_onEnableCookies directly controls the state.
        consentAccepted = enable;
        handleConsentState(enable, handlerOptions);
    };

    const observer = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutation) => {
            mutation.addedNodes.forEach((addedNode) => {
                if (addedNode instanceof HTMLElement) {
                    processAddedNode(addedNode);
                }
            });
        });
    });
    observer.observe(document.body, { subtree: true, childList: true });
}
